import { type Permission } from "@sarva/domain-types";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Tooltip } from "../primitives/Tooltip";

/**
 * (H) doc 27 §7: UI permission checks are ergonomics, never security. The API
 * is the authority; the UI merely avoids offering impossible actions, and PHI
 * masking happens server-side.
 */
export interface PermissionState {
  permissions: ReadonlySet<Permission>;
}

const PermissionContext = createContext<PermissionState | null>(null);

export function PermissionProvider({
  permissions,
  children,
}: {
  permissions: Iterable<Permission>;
  children: ReactNode;
}) {
  const value = useMemo<PermissionState>(
    () => ({ permissions: new Set(permissions) }),
    [permissions],
  );
  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions(): PermissionState {
  const ctx = useContext(PermissionContext);
  if (ctx === null) {
    throw new Error("Wrap the app in <PermissionProvider> before using permission checks.");
  }
  return ctx;
}

export function useHasPermission(...required: readonly Permission[]): boolean {
  const { permissions } = usePermissions();
  return required.every((p) => permissions.has(p));
}

/** Builds the 403 explanation from the generated error catalogue, not a literal. */
/**
 * `charge_batch:release` → "release charge batches". v3 (doc 41 §6): no
 * permission keys in copy — a practice admin reads the capability in words.
 * The key itself stays on `data-missing-permissions` for tests and support.
 */
export function humanizePermission(permission: Permission): string {
  const [resource = "", action = ""] = String(permission).split(":");
  const noun = resource.replace(/_/g, " ");
  const plural = noun.endsWith("s")
    ? noun
    : noun.endsWith("y")
      ? `${noun.slice(0, -1)}ies`
      : noun.endsWith("ch") || noun.endsWith("sh") || noun.endsWith("x")
        ? `${noun}es`
        : `${noun}s`;
  return `${action.replace(/_/g, " ")} ${plural}`.trim();
}

export function permissionDeniedMessage(missing: readonly Permission[]): string {
  const what = missing.map(humanizePermission);
  const list =
    what.length <= 1 ? (what[0] ?? "") : `${what.slice(0, -1).join(", ")} and ${what[what.length - 1]}`;
  return `You need access to ${list}. Ask an administrator.`;
}

export interface PermissionGateProps {
  /** From the generated `Permission` enum. (H) Never a string literal. */
  permission: Permission | readonly Permission[];
  /**
   * `disable` (the default) keeps the control visible and explains why it is
   * unavailable. `hide` removes it, and is correct only where the existence of
   * the capability itself must not be revealed (doc 27 §7).
   */
  mode?: "disable" | "hide";
  /** Rendered instead of the children when access is withheld. */
  fallback?: ReactNode;
  children: ReactNode;
}

/** (H) The only way UI is conditionally shown by permission (doc 27 §3). */
export function PermissionGate({
  permission,
  mode = "disable",
  fallback,
  children,
}: PermissionGateProps) {
  const { permissions } = usePermissions();
  const required = Array.isArray(permission) ? permission : [permission as Permission];
  const missing = required.filter((p) => !permissions.has(p));

  if (missing.length === 0) return <>{children}</>;

  if (fallback !== undefined) return <>{fallback}</>;

  if (mode === "hide") return null;

  const explanation = permissionDeniedMessage(missing);
  return (
    // (H) doc 27 §7: disabled-with-explanation, never silent grey. A greyed
    // control with no reason reads as a fault in the product rather than as a
    // permission boundary, and the user has no idea what to ask for.
    //
    // TWO nested spans, and the split is the whole point. The outer one is the
    // tooltip's trigger, so it must stay hoverable AND focusable — an earlier
    // version put `pointer-events-none` and `inert` on the single element
    // Radix uses as the trigger, which meant the explanation existed in the
    // markup and could never be seen. The inner one is `inert`, so the control
    // is genuinely unusable rather than merely styled that way.
    <Tooltip content={explanation}>
      <span
        data-permission-gate="denied"
        data-missing-permissions={missing.join(" ")}
        aria-disabled="true"
        // Focusable, so a keyboard user reaches the reason too. `tabIndex={0}`
        // on a non-interactive element is correct here precisely because the
        // reason is the only thing left to interact with.
        tabIndex={0}
        role="button"
        // `relative` holds the `sr-only` reason (absolutely positioned) inside this span.
        // Without it the reason's containing block is the viewport, so it escapes every
        // scrolling ancestor and a gated row low in the rail made the whole page scroll.
        className={cn(
          "relative inline-flex cursor-not-allowed rounded-sm",
          "outline-none focus-visible:outline focus-visible:outline-2",
          "focus-visible:outline-offset-2 focus-visible:outline-strong",
        )}
      >
        <span className="sr-only">{explanation}</span>
        <span inert className="pointer-events-none inline-flex opacity-60">
          {children}
        </span>
      </span>
    </Tooltip>
  );
}
